import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DashboardMeta } from "@/components/DashboardMeta";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Plus, Users, Sparkles, Droplets, UserPlus, HandCoins, UsersRound, TrendingUp, TrendingDown, Trash2, Edit3, Baby, GraduationCap, Video,
  ArrowRight, AlertCircle, CheckCircle2, Database, RefreshCw, Calculator,
} from "lucide-react";
import { format, parseISO } from "date-fns";

const KPI_META = {
  attendance:             { label: "Worship Center Attendance", shortLabel: "Worship Center",  icon: Users,          color: "#B2674A", isCurrency: false },
  fc_kids_attendance:     { label: "FC Kids Attendance",        shortLabel: "FC Kids",          icon: Baby,           color: "#DCA591", isCurrency: false },
  fc_students_attendance: { label: "FC Students Attendance",    shortLabel: "FC Students",      icon: GraduationCap,  color: "#AEBDB1", isCurrency: false },
  online_audience:        { label: "Online Audience",           shortLabel: "Online",           icon: Video,          color: "#425252", isCurrency: false },
  // Derived: auto-calculated from attendance + fc_kids_attendance. Read-only;
  // no manual entry, no edit, no delete (UI hides those affordances).
  total_sunday_attendance:{ label: "Total Sunday Attendance",   shortLabel: "Total Sunday",     icon: Calculator,     color: "#7FA1B5", isCurrency: false, derived: true },
  salvations:  { label: "Salvations",   icon: Sparkles,    color: "#C69E5F", isCurrency: false },
  baptisms:    { label: "Baptisms",     icon: Droplets,    color: "#AEBDB1", isCurrency: false },
  giving:      { label: "Giving",       icon: HandCoins,   color: "#B2674A", isCurrency: true  },
  guests:      { label: "Guests",       icon: UserPlus,    color: "#DCA591", isCurrency: false },
  volunteers:  { label: "Serving",      icon: UsersRound,  color: "#425252", isCurrency: false },
  small_groups:{ label: "Small Groups", icon: TrendingUp,  color: "#C69E5F", isCurrency: false },
};

// CATEGORY_ORDER places total_sunday_attendance right after the three
// per-service attendance categories so it reads as the natural roll-up.
// NOTE: "volunteers" (Serving) is intentionally omitted — the tile was
// removed from the KPI dashboard because Pushpay Serving isn't wired up
// yet and the category had no usable data. KPI_META['volunteers'] and the
// backend `volunteers` category are preserved so any historical rows in
// the database still validate; they simply no longer surface in the UI.
const CATEGORY_ORDER = ["attendance", "fc_kids_attendance", "total_sunday_attendance", "fc_students_attendance", "online_audience", "giving", "salvations", "baptisms", "guests", "small_groups"];
const ATTENDANCE_CATS = new Set(["attendance", "fc_kids_attendance", "total_sunday_attendance", "fc_students_attendance", "online_audience"]);

function fmt(val, isCurrency) {
  if (val === null || val === undefined) return "—";
  if (isCurrency) return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return Number(val).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function deltaPct(current, prev) {
  if (prev === null || prev === undefined || prev === 0) return null;
  return ((current - prev) / prev) * 100;
}

function SourceBadge({ source }) {
  if (source === "manual")
    return <Badge className="rounded-full bg-sage/20 text-brand-primary border-0 text-[10px] uppercase tracking-wider">Manual</Badge>;
  if (source === "derived")
    return <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/70 border-0 text-[10px] uppercase tracking-wider">Auto-calculated</Badge>;
  if (source === "pushpay_auto")
    return <Badge className="rounded-full bg-terracotta/10 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay · auto</Badge>;
  if (source === "pushpay_auto_locked")
    return <Badge className="rounded-full bg-brand-primary/10 text-brand-primary border-0 text-[10px] uppercase tracking-wider">Pushpay · locked</Badge>;
  if (source === "pushpay_placeholder")
    return <Badge className="rounded-full bg-peach/30 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay · not connected</Badge>;
  if (source === "pushpay")
    return <Badge className="rounded-full bg-terracotta/15 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay · live</Badge>;
  if (source === "ccb")
    return <Badge className="rounded-full bg-terracotta/15 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay ChMS · live</Badge>;
  if (source === "ccb_not_connected")
    return <Badge className="rounded-full bg-peach/30 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay ChMS · not connected</Badge>;
  if (source === "ccb_unavailable")
    return <Badge className="rounded-full bg-peach/25 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay ChMS · not available</Badge>;
  if (source === "ccb_error")
    return <Badge className="rounded-full bg-peach/40 text-terracotta border-0 text-[10px] uppercase tracking-wider">Pushpay ChMS · error</Badge>;
  return null;
}

function DateRangeLabel({ rule, windows }) {
  if (!windows) return null;
  const fmtD = (iso) => format(parseISO(iso), "MMM d");
  if (rule === "weekly_total") {
    return <>Week of {fmtD(windows.week_start)}</>;
  }
  if (rule === "monthly_average_ytd") {
    return <>Monthly avg · {fmtD(windows.year_start)} – {fmtD(windows.today)}</>;
  }
  // ytd_total or weekly_average_ytd
  return <>YTD · {fmtD(windows.year_start)} – {fmtD(windows.today)}</>;
}

export default function KPI() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("attendance");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category: "attendance", value: "", week_of: "", notes: "" });
  const [pendingDelete, setPendingDelete] = useState(null); // {id, week_of, value} | null
  const [deleting, setDeleting] = useState(false);

  // Backend KPI write endpoints are admin-only (`require_admin` on POST/PATCH/DELETE).
  // Mirror that here so non-admins never see buttons that silently 403.
  const canEdit = user?.role === "admin";

  const load = async ({ force = false, silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Fetch summary FIRST — it triggers backend auto-persistence of CCB
      // (Pushpay ChMS) live counts into kpi_entries. Fetching entries before
      // summary would race and miss the just-created `pushpay_auto` rows.
      const sum = await api.get(`/kpi-summary${force ? "?nocache=1" : ""}`);
      const rows = await api.get("/kpi-entries");
      setKpis(rows.data);
      setSummary(sum.data);
      setLoadError(null);
    } catch (e) {
      if (!silent) setLoadError(e?.response?.data?.detail || "Unable to load KPI data");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Silent background refresh every 60s. `silent:true` keeps the Refresh button
  // from spinning so the UI stays stable while a staff member reads the cards.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      load({ silent: true });
    }, 60_000);
    return () => clearInterval(id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Force a fresh fetch the moment the tab regains focus so staff returning
  // from lunch see up-to-date numbers without having to click Refresh.
  // `?nocache=1` bypasses the server cache, `silent:true` keeps the spinner off.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load({ force: true, silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const seriesByCat = useMemo(() => {
    const m = {};
    for (const k of kpis) {
      (m[k.category] = m[k.category] || []).push(k);
    }
    Object.keys(m).forEach((k) => m[k].sort((a, b) => a.week_of.localeCompare(b.week_of)));
    return m;
  }, [kpis]);

  const openCreate = () => {
    const today = new Date();
    const days_since_sunday = (today.getDay()); // 0=Sun
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - days_since_sunday);
    const weekOf = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, "0")}-${String(sunday.getDate()).padStart(2, "0")}`;
    setEditingId(null);
    setForm({ category: tab, value: "", week_of: weekOf, notes: "" });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({ category: row.category, value: row.value, week_of: row.week_of, notes: row.notes || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.value || !form.week_of) {
      toast.error("Value and week date required");
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/kpi-entries/${editingId}`, {
          value: parseFloat(form.value),
          week_of: form.week_of,
          notes: form.notes || null,
        });
        toast.success("Entry updated");
      } else {
        await api.post("/kpi-entries", { ...form, value: parseFloat(form.value) });
        toast.success("Entry saved");
      }
      setOpen(false);
      setEditingId(null);
      setForm({ category: tab, value: "", week_of: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to save");
    }
  };

  const del = (row) => setPendingDelete(row);

  const confirmDelete = async () => {
    if (!pendingDelete?.id) return;
    const id = pendingDelete.id;
    setDeleting(true);
    // Optimistically remove the row so the table reacts instantly.
    const snapshot = kpis;
    setKpis((prev) => prev.filter((k) => k.id !== id));
    try {
      await api.delete(`/kpi-entries/${id}`);
      toast.success("Entry deleted");
      setPendingDelete(null);
      // Refresh summary (cache was invalidated server-side) so the rollup cards reflect the change.
      load({ force: true, silent: true });
    } catch (e) {
      // Roll back the optimistic removal so the user can see exactly what failed.
      setKpis(snapshot);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(
        status === 403
          ? "Only an admin can delete KPI entries."
          : status === 404
          ? "This entry no longer exists — the list has been refreshed."
          : detail || "Unable to delete this entry. Please try again."
      );
      if (status === 404) load({ force: true, silent: true });
    } finally {
      setDeleting(false);
    }
  };

  const cards = summary?.cards || {};
  const windows = summary?.windows;

  return (
    <div className="space-y-8" data-testid="kpi-dashboard">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Weekly staff review</p>
          <h2 className="font-heading text-2xl sm:text-3xl text-brand-primary mt-1">KPI dashboard</h2>
          <p className="text-sm text-brand-primary/70 mt-1">
            All metrics rolled up using your reporting rules. Timezone: {summary?.timezone || "America/Chicago"}.
          </p>
          <div className="mt-2">
            <DashboardMeta
              fetchedAt={summary?.fetched_at}
              loading={loading}
              error={loadError || (summary?.pushpay_error ? `Pushpay: ${summary.pushpay_error}` : null)}
              connected={summary ? !!summary.pushpay_live : undefined}
              connectedLabel="Pushpay not connected"
              onRefresh={() => load({ force: true })}
            />
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <BackfillButton onDone={() => load({ force: true })} />
            <Button
              data-testid="add-kpi-button"
              onClick={openCreate}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-11 px-5 shadow-warm"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add / override value
            </Button>
          </div>
        )}
      </div>

      {/* Pushpay (Giving) not-connected banner */}
      {summary && !summary.pushpay_live && (
        <Card className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4" data-testid="pushpay-banner">
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Pushpay (Giving) is not connected yet.</p>
            <p className="text-sm text-brand-primary/70 mt-1">
              The Giving card is showing placeholder data. Guests and Small Groups now pull from Pushpay ChMS (CCB) — their status is shown on each card badge. Connect Pushpay Giving in <a href="/settings" className="text-terracotta hover:underline">Settings → Integrations → Pushpay</a>; see the <a href="#pushpay-mapping" className="text-terracotta hover:underline">Pushpay field mapping</a> below for the exact endpoints.
            </p>
          </div>
        </Card>
      )}

      {/* Primary summary cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="kpi-summary-grid">
        {CATEGORY_ORDER.map((cat) => {
          const meta = KPI_META[cat];
          const card = cards[cat];
          if (!card) return null;
          const Icon = meta.icon;
          const delta = deltaPct(card.primary_value, card.prev_period_value);
          const Trend = delta == null ? null : (delta >= 0 ? TrendingUp : TrendingDown);
          const trendColor = delta == null ? "text-brand-primary/50" : delta >= 0 ? "text-sage" : "text-terracotta";
          return (
            <Card
              key={cat}
              data-testid={`kpi-card-${cat}`}
              className={`rounded-2xl bg-white border shadow-warm p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg cursor-pointer ${
                tab === cat ? "border-terracotta ring-2 ring-terracotta/15" : "border-brand-primary/10"
              }`}
              onClick={() => {
                // Giving has a dedicated drill-down page with fund/type breakdowns.
                if (cat === "giving") {
                  navigate("/giving");
                  return;
                }
                setTab(cat);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${meta.color}1A` }}>
                  <Icon className="h-5 w-5" style={{ color: meta.color }} strokeWidth={1.7} />
                </div>
                <SourceBadge source={card.source} />
              </div>
              <p className="text-sm text-brand-primary/70 mt-5">{meta.label}</p>
              <p className="font-heading text-4xl font-light text-brand-primary tracking-tight mt-1" data-testid={`kpi-primary-${cat}`}>
                {fmt(card.primary_value, meta.isCurrency)}
              </p>
              <div className="flex items-center justify-between mt-2">
                <p className="text-[11px] uppercase tracking-wider text-terracotta font-semibold">
                  {card.rule_label}
                </p>
                {Trend && (
                  <span className={`text-xs font-medium inline-flex items-center gap-1 ${trendColor}`}>
                    <Trend className="h-3 w-3" />
                    {Math.abs(delta).toFixed(1)}%
                  </span>
                )}
              </div>
              <p className="text-xs text-brand-primary/55 mt-1">
                <DateRangeLabel rule={card.rule} windows={windows} />
              </p>

              {/* Mini snapshot row */}
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-brand-primary/10">
                {ATTENDANCE_CATS.has(cat) ? (
                  <>
                    <Snapshot label="This week" value={card.this_week_value} isCurrency={meta.isCurrency} testId={`kpi-snapshot-this-week-${cat}`} />
                    <Snapshot label="Last week" value={card.last_week_value} isCurrency={meta.isCurrency} testId={`kpi-snapshot-last-week-${cat}`} />
                    <GrowthSnapshot value={card.ytd_growth_pct} testId={`kpi-snapshot-ytd-growth-${cat}`} />
                  </>
                ) : cat === "volunteers" ? (
                  <>
                    <Snapshot label="Accepted" value={card.accepted_this_week} testId={`kpi-snapshot-accepted-${cat}`} />
                    <Snapshot label="Declined" value={card.declined_this_week} testId={`kpi-snapshot-declined-${cat}`} />
                    <GrowthSnapshot value={card.monthly_acceptance_pct} label="Monthly acceptance" testId={`kpi-snapshot-monthly-acceptance-${cat}`} />
                  </>
                ) : cat === "giving" ? (
                  <>
                    <Snapshot label="This month" value={card.this_month} isCurrency testId={`kpi-snapshot-this-month-${cat}`} />
                    <Snapshot label="Last month" value={card.last_month_value} isCurrency testId={`kpi-snapshot-last-month-${cat}`} />
                    <GrowthSnapshot value={card.mom_pct} label="MoM change" testId={`kpi-snapshot-mom-${cat}`} />
                  </>
                ) : (
                  <>
                    <Snapshot label="This week" value={card.this_week} isCurrency={meta.isCurrency} />
                    <Snapshot label="This month" value={card.this_month} isCurrency={meta.isCurrency} />
                    <Snapshot label="YTD" value={card.ytd_total} isCurrency={meta.isCurrency} />
                  </>
                )}
              </div>
              {card.source === "ccb_unavailable" && card.error && (
                <p className="text-[11px] text-terracotta/90 mt-3 leading-snug" data-testid={`kpi-note-${cat}`}>
                  {card.error}
                </p>
              )}
            </Card>
          );
        })}
      </section>

      {/* Tabs + chart + table */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-5">
        <TabsList className="bg-white border border-brand-primary/10 rounded-full p-1 h-auto flex-wrap">
          {CATEGORY_ORDER.map((c) => (
            <TabsTrigger
              key={c}
              value={c}
              data-testid={`kpi-tab-${c}`}
              className="rounded-full data-[state=active]:bg-terracotta data-[state=active]:text-white px-4 py-1.5 text-sm"
            >
              {KPI_META[c].label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabChart
          tab={tab}
          series={seriesByCat[tab] || []}
          meta={KPI_META[tab]}
          card={cards[tab]}
        />

        <KpiTable
          rows={(seriesByCat[tab] || []).slice().reverse()}
          meta={KPI_META[tab]}
          canEdit={canEdit}
          onEdit={openEdit}
          onDelete={del}
        />
      </Tabs>

      {/* Pushpay mapping reference */}
      <section id="pushpay-mapping" className="pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Database className="h-4 w-4 text-terracotta" />
          <h3 className="font-heading text-xl text-brand-primary">Pushpay field mapping reference</h3>
        </div>
        <p className="text-sm text-brand-primary/65 mb-4 max-w-3xl">
          When Pushpay is connected, each KPI below will pull directly from these endpoints. If the real Pushpay schema differs, send us a sample response and we'll adjust the mapping — no layout changes required.
        </p>
        <Accordion type="single" collapsible className="bg-white rounded-2xl border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="pushpay-mapping-accordion">
          {/* Pushpay Serving mapping (`volunteers`) intentionally omitted:
              Pushpay Serving/Scheduler isn't connected yet and the KPI has
              been removed from the dashboard until it is. */}
          {["giving", "guests", "small_groups"].map((cat) => {
            const m = cards[cat]?.pushpay_mapping;
            if (!m) return null;
            const Icon = KPI_META[cat].icon;
            return (
              <AccordionItem key={cat} value={cat} className="border-b border-brand-primary/10 last:border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-cream/60 text-left">
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-terracotta" strokeWidth={1.7} />
                    <span className="font-heading text-base text-brand-primary">{KPI_META[cat].label}</span>
                    <Badge className="rounded-full bg-peach/30 text-terracotta border-0 text-[10px]">{cards[cat].rule_label}</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 text-sm text-brand-primary/80 space-y-2">
                  <p><strong className="text-brand-primary">Endpoint:</strong> <code className="font-mono text-xs bg-cream px-2 py-0.5 rounded">{m.endpoint}</code></p>
                  <p><strong className="text-brand-primary">Object:</strong> {m.object}</p>
                  <p><strong className="text-brand-primary">Fields needed:</strong> {m.fields.join(", ")}</p>
                  <p><strong className="text-brand-primary">Aggregation:</strong> {m.aggregation}</p>
                  <p><strong className="text-brand-primary">Docs:</strong> <a href={m.docs} target="_blank" rel="noreferrer" className="text-terracotta hover:underline">{m.docs}</a></p>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o && !deleting) setPendingDelete(null); }}
      >
        <AlertDialogContent data-testid="delete-kpi-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this KPI entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? (
                <>
                  You're about to delete the <strong>{KPI_META[pendingDelete.category]?.label || pendingDelete.category}</strong> entry
                  for the week of <strong>{format(parseISO(pendingDelete.week_of), "MMM d, yyyy")}</strong>
                  {" "}(<strong>{fmt(pendingDelete.value, KPI_META[pendingDelete.category]?.isCurrency)}</strong>).
                  This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-kpi-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-kpi-confirm-button"
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-terracotta hover:bg-terracotta-hover text-white"
            >
              {deleting ? "Deleting..." : "Delete entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? "Edit KPI entry" : "Add / override KPI entry"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-brand-primary/60 uppercase tracking-wider">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={!!editingId}>
                <SelectTrigger data-testid="kpi-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* Derived categories (e.g., total_sunday_attendance) are
                      excluded — they're auto-calculated and would 400 on POST. */}
                  {CATEGORY_ORDER.filter((c) => !KPI_META[c]?.derived).map((c) => (
                    <SelectItem key={c} value={c}>{KPI_META[c].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-brand-primary/60 uppercase tracking-wider">Week of (Sunday)</label>
              <Input
                data-testid="kpi-week-input"
                type="date"
                value={form.week_of}
                onChange={(e) => setForm({ ...form, week_of: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs text-brand-primary/60 uppercase tracking-wider">
                Value {KPI_META[form.category]?.isCurrency ? "(USD)" : ""}
              </label>
              <Input
                data-testid="kpi-value-input"
                type="number"
                step="0.01"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder={KPI_META[form.category]?.isCurrency ? "e.g. 74800" : "e.g. 452"}
              />
            </div>
            <Textarea
              data-testid="kpi-notes-input"
              rows={3}
              placeholder="Notes (optional) — e.g. 'Special Baptism Sunday service'"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            {["giving", "guests", "small_groups"].includes(form.category) && (
              <p className="text-xs text-brand-primary/60 bg-peach/15 rounded-lg px-3 py-2">
                <AlertCircle className="h-3 w-3 inline mr-1 text-terracotta" />
                This KPI is normally sourced from Pushpay. A manual entry here acts as an override for that week.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              data-testid="add-kpi-submit"
              onClick={submit}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
            >
              {editingId ? "Save changes" : "Save entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Snapshot({ label, value, isCurrency, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-brand-primary/50 font-semibold">{label}</p>
      <p className="text-sm font-medium text-brand-primary mt-0.5">{fmt(value, isCurrency)}</p>
    </div>
  );
}

// --- Admin-only YTD backfill button. Calls POST /api/kpi-entries/backfill
//     to fill historical Guests + Small Groups weeks with real Pushpay ChMS
//     counts. Idempotent on the backend so re-runs are safe.
function BackfillButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/kpi-entries/backfill?category=all");
      setReport(data);
      const total = (data?.summary?.guests?.created || 0) + (data?.summary?.small_groups?.created || 0);
      toast.success(total ? `Backfill: ${total} new weekly entries created.` : "Backfill: no new entries — already up to date.");
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) setReport(null); } }}>
      <DialogTrigger asChild>
        <Button
          data-testid="backfill-button"
          variant="outline"
          className="rounded-full border-brand-primary/15 h-10 px-4"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" /> Backfill YTD
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="backfill-dialog">
        <DialogHeader>
          <DialogTitle>Backfill YTD from Pushpay ChMS</DialogTitle>
        </DialogHeader>
        {!report ? (
          <>
            <p className="text-sm text-brand-primary/70">
              This pulls Guests (new profiles) and Small Groups (Regular Events only)
              from Pushpay ChMS for every Sunday-Saturday week from January 1 through
              the week before this one, and creates one <code className="font-mono text-xs">pushpay_auto</code> entry
              per missing week.
            </p>
            <ul className="text-xs text-brand-primary/60 list-disc ml-5 space-y-1">
              <li>Idempotent — safe to re-run; existing entries are never overwritten.</li>
              <li>Manual entries always win, never replaced.</li>
              <li>Weeks with zero data are skipped (gap in chart, not a fake zero).</li>
              <li>Historical entries are auto-locked once the next week begins.</li>
            </ul>
            <DialogFooter>
              <Button data-testid="backfill-cancel" variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="backfill-run" disabled={busy} onClick={run} className="bg-terracotta hover:bg-terracotta-hover text-white">
                {busy ? "Running..." : "Run backfill"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-2 text-sm" data-testid="backfill-report">
              {["guests", "small_groups"].map((cat) => {
                const s = report.summary?.[cat]; if (!s) return null;
                return (
                  <div key={cat} className="border border-brand-primary/10 rounded-xl p-3">
                    <p className="font-medium text-brand-primary capitalize">{cat.replace("_", " ")}</p>
                    <p className="text-xs text-brand-primary/70 mt-1">
                      Created <strong>{s.created}</strong>, skipped existing <strong>{s.skipped_existing}</strong>,
                      skipped zero weeks <strong>{s.skipped_zero}</strong>, errors <strong>{s.errors}</strong>.
                    </p>
                  </div>
                );
              })}
              <p className="text-[11px] text-brand-primary/50">
                Range: {report.year_start} → week before {report.current_week_start} ({report.weeks_in_range} weeks examined).
              </p>
            </div>
            <DialogFooter>
              <Button data-testid="backfill-close" onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GrowthSnapshot({ value, testId, label = "YTD Growth" }) {
  const hasValue = value !== null && value !== undefined;
  const positive = hasValue && value >= 0;
  const display = hasValue ? `${positive ? "+" : ""}${Number(value).toFixed(1)}%` : "—";
  const tone = !hasValue ? "text-brand-primary" : positive ? "text-sage" : "text-terracotta";
  return (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-brand-primary/50 font-semibold">{label}</p>
      <p className={`text-sm font-medium mt-0.5 ${tone}`}>{display}</p>
    </div>
  );
}

function TabChart({ tab, series, meta, card }) {
  const formatted = series.map((s) => ({ ...s, label: format(parseISO(s.week_of), "MMM d") }));
  // `tab === "volunteers"` is kept defensive so if any legacy state ever
  // routes here, the chart renders as a bar (previously the Serving KPI's
  // preferred style). The volunteers tab itself is no longer selectable.
  const useBar = tab === "giving" || tab === "volunteers" || tab === "guests" || tab === "small_groups";
  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">{meta.label}</p>
          <h3 className="font-heading text-xl text-brand-primary mt-1">Weekly trend</h3>
        </div>
        {card && (
          <div className="flex items-center gap-2 text-sm">
            <Badge className="rounded-full bg-cream text-brand-primary border-brand-primary/15">{card.rule_label}</Badge>
            <ArrowRight className="h-3 w-3 text-brand-primary/40" />
            <span className="font-medium text-brand-primary">{fmt(card.primary_value, meta.isCurrency)}</span>
          </div>
        )}
      </div>
      <div className="h-72">
        {formatted.length === 0 ? (
          <div className="h-full grid place-items-center text-brand-primary/55 text-sm">
            No data yet — add weekly entries to see the trend.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {useBar ? (
              <BarChart data={formatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#42525220" />
                <XAxis dataKey="label" stroke="#42525280" fontSize={12} />
                <YAxis stroke="#42525280" fontSize={12} tickFormatter={(v) => meta.isCurrency ? `$${(v/1000).toFixed(0)}k` : v} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #4252521a" }}
                  formatter={(v) => meta.isCurrency ? `$${Number(v).toLocaleString()}` : Number(v).toLocaleString()}
                />
                <Bar dataKey="value" fill={meta.color} radius={[8, 8, 0, 0]} />
              </BarChart>
            ) : (
              <LineChart data={formatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#42525220" />
                <XAxis dataKey="label" stroke="#42525280" fontSize={12} />
                <YAxis stroke="#42525280" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #4252521a" }} />
                <Line type="monotone" dataKey="value" stroke={meta.color} strokeWidth={2.5} dot={{ r: 4, fill: meta.color }} />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

function KpiTable({ rows, meta, canEdit, onEdit, onDelete }) {
  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="kpi-entries-table">
      <div className="px-6 py-5 border-b border-brand-primary/10 flex items-center justify-between">
        <h3 className="font-heading text-xl text-brand-primary">All {meta.label.toLowerCase()} entries</h3>
        <p className="text-xs text-brand-primary/55">{rows.length} {rows.length === 1 ? "entry" : "entries"}</p>
      </div>
      <div className="overflow-x-auto"><table className="w-full min-w-[640px]">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-brand-primary/55 bg-cream/50">
            <th className="px-6 py-3 font-medium">Week of</th>
            <th className="px-6 py-3 font-medium">Value</th>
            <th className="px-6 py-3 font-medium">Notes</th>
            <th className="px-6 py-3 font-medium">Source</th>
            <th className="px-6 py-3 font-medium">Added</th>
            <th className="px-6 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-brand-primary/10">
          {rows.length === 0 && (
            <tr><td colSpan={6} className="px-6 py-8 text-center text-brand-primary/60">No entries yet.</td></tr>
          )}
          {rows.map((k) => (
            <tr key={k.id} className="hover:bg-cream/40">
              <td className="px-6 py-4 text-brand-primary font-medium">{format(parseISO(k.week_of), "MMM d, yyyy")}</td>
              <td className="px-6 py-4 text-brand-primary">{fmt(k.value, meta.isCurrency)}</td>
              <td className="px-6 py-4 text-brand-primary/70 max-w-[22rem] truncate">{k.notes || "—"}</td>
              <td className="px-6 py-4"><SourceBadge source={k.source || "manual"} /></td>
              <td className="px-6 py-4 text-brand-primary/55 text-sm">{format(parseISO(k.created_at), "MMM d")}</td>
              <td className="px-6 py-4 text-right">
                {canEdit && k.source !== "derived" && (
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => onEdit(k)} data-testid={`edit-kpi-${k.id}`} className="text-brand-primary/40 hover:text-terracotta p-1">
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => onDelete(k)} data-testid={`delete-kpi-${k.id}`} className="text-brand-primary/40 hover:text-terracotta p-1" aria-label="Delete entry">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {k.source === "derived" && (
                  <span className="text-[10px] uppercase tracking-wider text-brand-primary/40">read-only</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </Card>
  );
}
